import { useEffect, useRef, useState } from 'react';
import VideoGenericElement from '$components/GenericElements/VideoGenericElement';
import { ReactComponent as Pencil } from '$icons/uncolored/pencil.svg';
import { CandidateData } from '$types/candidates';
import { useTranslation } from 'react-i18next';
import {
  useGetAllVideoFeedbacksQuery,
  useSendVideoCommentMutation,
} from '$store/modules/video-feedback';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  useGetRatingEmployerQuery,
  useGetRatingInternalQuery,
  useUpdateRatingMutation,
} from '$store/modules/ratings';
import SPKToast from '$components/SPKToast/SPKToast';
import getUserType from '$utils/getUserType';
import { UserTypes } from '$types/UserTypes';
import { tracker } from '$utils/tracker';

import { Button } from 'primereact/button';
import SPKShowcaseCandidateInfo from '../SPKShowcaseCandidateInfo/SPKShowcaseCandidateInfo';
import SPKEmployerCandidateVideosRate from './SPKEmployerCandidateVideosRate';

const SPKEmployerShowcaseVideoModal = ({
  selectedVideo,
  setSelectedVideo,
  candidate,
}: {
  selectedVideo: any;
  setSelectedVideo: (prop: any) => void;
  candidate: CandidateData;
}) => {
  const [t] = useTranslation('global');
  const [videoRating, setVideoRating] = useState(-1);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const userType = getUserType();

  const { data, isLoading } = useGetAllVideoFeedbacksQuery({
    videoId: selectedVideo.Id,
    videoType: 'AdditionalVideos',
  });

  const { data: employerRate } = useGetRatingEmployerQuery(
    {
      data: {
        additionalVideoId: selectedVideo.Id,
      },
    },
    { skip: userType !== UserTypes.EMPLOYER }
  );

  const { data: internalRate } = useGetRatingInternalQuery(
    {
      data: {
        additionalVideoId: selectedVideo.Id,
      },
    },
    { skip: userType !== UserTypes.INTERNAL }
  );

  const { register, watch, setValue, handleSubmit } = useForm({
    defaultValues: {
      feedbackComment: '',
    },
  });

  const feedbackCommentValue = watch('feedbackComment');

  const [sendComment] = useSendVideoCommentMutation();

  const [updateRating] = useUpdateRatingMutation();

  const onSubmitFeedbackHandler: SubmitHandler<{ feedbackComment: string }> = ({
    feedbackComment,
  }) => {
    Promise.all([
      sendComment({
        videoId: selectedVideo.Id,
        videoType: 'AdditionalVideos',
        videoComment: feedbackComment,
      }),
      updateRating({
        data: {
          additionalVideoId: selectedVideo.Id,
          rate: videoRating + 1,
        },
        hideToast: true,
      }),
    ]).then(() => {
      tracker.emit('Video Rating', {
        video_id: selectedVideo.Id,
        video_type: 'AdditionalVideos',
        candidate_id: candidate.Id,
        rating: videoRating + 1,
      });
      SPKToast(t('VideoModal.FeedbackSent'));
      setSelectedVideo(undefined);
    });
  };

  const onSetVideoRatingHandler = (rating: number) => {
    setVideoRating(rating);
    if (videoContainerRef.current)
      videoContainerRef.current.scrollTo({
        top: videoContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
  };

  useEffect(() => {
    if (data) setValue('feedbackComment', data?.[0]?.Comment || '');
  }, [data, setValue]);

  useEffect(() => {
    if (employerRate) setVideoRating(employerRate?.Rating || -1);
  }, [employerRate]);

  useEffect(() => {
    if (internalRate) setVideoRating(internalRate?.Rating || -1);
  }, [internalRate]);

  return (
    <div
      ref={videoContainerRef}
      className="flex flex-col overflow-auto bg-white py-5 px-4 rounded-2xl w-full"
    >
      <SPKShowcaseCandidateInfo candidate={candidate} />
      <div className="mb-4 text-spk-highContrast text-base font-bold">
        {selectedVideo.Name}
      </div>
      <VideoGenericElement
        videoData={selectedVideo}
        style={{
          maxWidth: '80%',
          maxHeight: '25rem',
          alignSelf: 'center',
        }}
        controls
        playsInline
        className="rounded-xl"
      />
      <div className="flex flex-col justify-center items-center">
        <div className="mt-4 text-spk-mediumContrast text-sm">
          {t('VideoModal.RateCandidate')}
        </div>
        <SPKEmployerCandidateVideosRate
          videoRating={videoRating}
          setVideoRating={onSetVideoRatingHandler}
        />
      </div>
      <div className="mt-6 text-xs text-spk-mediumContrast">
        {t('VideoModal.FeedbackReason')}
      </div>
      <form onSubmit={handleSubmit(onSubmitFeedbackHandler)}>
        <fieldset disabled={isLoading}>
          <div className="mb-8 relative">
            <textarea
              className="outline-none min-h-24 w-full border border-employer-secondary rounded-md flex justify-start overflow-hidden p-2 text-sm"
              {...register('feedbackComment')}
            />
            {feedbackCommentValue.length === 0 && (
              <Pencil className="fill-spk-mediumContrast absolute top-3 left-3" />
            )}
            <div className="text-xs text-spk-highContrast">
              {t('VideoModal.InternalUseOnly')}
            </div>
          </div>
          <Button
            type="submit"
            label={t('VideoModal.SendFeedback')}
            severity="success"
            className="ml-auto flex"
          />
        </fieldset>
      </form>
    </div>
  );
};

export default SPKEmployerShowcaseVideoModal;
